import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider
} from "@mui/material";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rental-rate-tabpanel-${index}`}
      aria-labelledby={`rental-rate-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          pt: 3,
          fontFamily: 'Arial, sans-serif',
          fontSize: '0.95rem',
          fontWeight: 'normal',
          fontStyle: 'normal',
          color: '#000000' // or color: 'black'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const RentalRateView = () => {
  const [rentalRates, setRentalRates] = useState([]);
  const [groupedRates, setGroupedRates] = useState({});
  const [parkNames, setParkNames] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({});

  // Fetch rental rates on component mount
  useEffect(() => {
    fetchRentalRates();
  }, []);

  const fetchRentalRates = async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await GetDataFromApiWithParams("finances/rentalrates");
      console.log("Rental rates response:", response);
      
      if (response && Array.isArray(response)) {
        setRentalRates(response);
        groupRatesByPark(response);
        calculateSummary(response);
      } else {
        setRentalRates([]);
        setError("No rental rate data found");
      }
    } catch (err) {
      console.error("Error fetching rental rates:", err);
      setError("Failed to fetch rental rates");
    } finally {
      setLoading(false);
    }
  };

  // Group rental rates by park name
  const groupRatesByPark = (data) => {
    const grouped = {};
    const parks = [];
    
    data.forEach(rate => {
      const parkName = rate.park_name || "Unassigned";
      
      if (!grouped[parkName]) {
        grouped[parkName] = [];
        parks.push(parkName);
      }
      
      grouped[parkName].push(rate);
    });
    
    // Sort parks alphabetically
    parks.sort();
    setParkNames(parks);
    setGroupedRates(grouped);
  };

  // Calculate summary statistics
  const calculateSummary = (data) => {
    const summary = {};
    
    data.forEach(rate => {
      const parkName = rate.park_name || "Unassigned";
      const propertyType = rate.type_of_property || "Unknown";
      
      if (!summary[parkName]) {
        summary[parkName] = {
          totalRecords: 0,
          propertyTypes: {},
          totalAmount: 0
        };
      }
      
      summary[parkName].totalRecords++;
      summary[parkName].totalAmount += parseFloat(rate.amount) || 0;
      
      if (!summary[parkName].propertyTypes[propertyType]) {
        summary[parkName].propertyTypes[propertyType] = 0;
      }
      summary[parkName].propertyTypes[propertyType]++;
    });
    
    setSummary(summary);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading rental rates...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', mb: 3 }}>
          Rental Rate Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {parkNames.length === 0 ? (
          <Alert severity="info">No rental rates found</Alert>
        ) : (
          <>
            {/* Tabs for each park */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={selectedTab} 
                onChange={handleTabChange} 
                variant="scrollable"
                scrollButtons="auto"
                aria-label="park rental rate tabs"
              >
                {parkNames.map((park, index) => (
                  <Tab 
                    key={index} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span sx={{ fontFamily: 'Arial, sans-serif' }}>{park}</span>
                    </Box>
                    } 
                  />
                ))}
              </Tabs>
            </Box>

            {/* Tab panels for each park */}
            {parkNames.map((park, index) => (
              <TabPanel key={index} value={selectedTab} index={index}>
                {/* Park Summary Card */}
                

                {/* Rental Rates Table */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f0f7ff' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>S.No</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Park ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Property Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>From </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>To </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Adjusted By</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedRates[park]?.map((rate, idx) => (
                        <TableRow key={rate.no || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{rate.park_id || '-'}</TableCell>
                          <TableCell>
                          {rate.type_of_property }
                            
                          </TableCell>
                          <TableCell>{rate.from}</TableCell>
                          <TableCell>{rate.to}</TableCell>
                          <TableCell>{rate.description || '-'}</TableCell>
                          <TableCell>{rate.adjusted_by || '-'}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {formatCurrency(rate.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {groupedRates[park]?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                            <Typography color="textSecondary">
                              No rental rates found for {park}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer with record count */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Records: {groupedRates[park]?.length || 0}
                  </Typography>
                </Box>
              </TabPanel>
            ))}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RentalRateView;